// The vertical (9:16) short version of the tutorial, for Reels / TikTok / Shorts. Same
// engine and screens as the full walkthrough, on a narrow 720×1000 stage without the
// sidebar, with a fast ~40s script. Narration: ElevenLabs eleven_v3 with-timestamps, and
// `words` are its word timings (the captions highlight each word as it is spoken).
import {
  Scene,
  finishScenes,
  set,
  type,
  count,
  scroll,
  spot,
  tap,
  td,
  DEMO_CAMPAIGN_NAME,
  DEMO_POST_TEXT,
  DEMO_FB_EMAIL,
  DEMO_LIBRARY_GROUPS,
  DEMO_GROUPS,
} from './demoScript'

export const REEL_STAGE_W = 720
export const REEL_STAGE_H = 1000

const READY = {hasCampaign: true, hasPost: true, groupCount: DEMO_LIBRARY_GROUPS, connected: true, campaignEnabled: true}

export const REEL_SCENES: Scene[] = finishScenes(
  [
    {
      id: 'hook',
      title: 'פרסום אוטומטי',
      headline: 'פרסום אוטומטי בפייסבוק 🚀',
      audio: '/media/tutorial/reel/r1-hook.mp3',
      audioMs: 6480,
      text: 'רוצים שהמודעות שלכם יתפרסמו בעשרות קבוצות בפייסבוק, בלי לגעת? ככה זה עובד. מתחברים למערכת,',
      words: [['רוצים', 0, 360], ['שהמודעות', 380, 880], ['שלכם', 920, 1240], ['יתפרסמו', 1280, 1760], ['בעשרות', 1800, 2280], ['קבוצות', 2320, 2640], ['בפייסבוק,', 2680, 3253], ['בלי', 3267, 3440], ['לגעת?', 3480, 4040], ['ככה', 4160, 4320], ['זה', 4400, 4480], ['עובד.', 4560, 5000], ['מתחברים', 5060, 5560], ['למערכת,', 5580, 6480]],
      init: {...READY, view: 'schedule', published: 1284, today: 36},
      cursorStart: {x: 520, y: 760},
      steps: [
        count(200, 'scheduleDone', 0, 6, 3800),
        spot(1800, td('sched-2')),
        spot(3900, null),
        set(4500, {screen: 'login'}),
        ...tap(4550, td('login-user'), 300),
        type(4950, 'loginUser', 'david@example.com', 45),
        ...tap(5400, td('login-pass'), 250),
        type(5750, 'loginPass', 'demo-pass', 40),
        ...tap(6050, td('login-submit'), 250),
        set(6420, {loginLoading: true}),
      ],
    },
    {
      id: 'campaign',
      title: 'קמפיין',
      headline: '1 · יוצרים קמפיין',
      audio: '/media/tutorial/reel/r2-campaign.mp3',
      audioMs: 3200,
      text: 'יוצרים קמפיין חדש, למשל: נכסים תל אביב,',
      words: [['יוצרים', 0, 400], ['קמפיין', 480, 800], ['חדש,', 880, 1280], ['למשל:', 1320, 2000], ['נכסים', 2160, 2560], ['תל', 2640, 2747], ['אביב,', 2773, 3200]],
      init: {},
      cursorStart: {x: 360, y: 700},
      steps: [
        scroll(0, td('campaigns-card')),
        ...tap(100, td('new-campaign'), 450),
        set(680, {campaignModal: true}),
        ...tap(850, td('camp-name'), 300),
        type(1250, 'campName', DEMO_CAMPAIGN_NAME, 11),
        ...tap(2650, td('camp-submit'), 350),
        set(3100, {campCreating: true}),
        set(3500, {campaignModal: false, campCreating: false, hasCampaign: true}),
        spot(3550, td('campaign-card')),
      ],
    },
    {
      id: 'post',
      title: 'פוסט + מרקטפלייס',
      headline: '2 · מוסיפים פוסט',
      audio: '/media/tutorial/reel/r3-post.mp3',
      audioMs: 4640,
      text: 'מוסיפים פוסט עם טקסט ותמונות, ובלחיצה אחת הוא יוצא גם למרקטפלייס.',
      words: [['מוסיפים', 0, 480], ['פוסט', 560, 840], ['עם', 880, 1000], ['טקסט', 1040, 1320], ['ותמונות,', 1360, 2080], ['ובלחיצה', 2240, 2640], ['אחת', 2720, 2960], ['הוא', 3000, 3093], ['יוצא', 3120, 3360], ['גם', 3520, 3640], ['למרקטפלייס.', 3660, 4640]],
      init: {hasCampaign: true},
      cursorStart: {x: 360, y: 500},
      steps: [
        scroll(0, td('posts-section')),
        ...tap(100, td('add-post'), 400),
        set(620, {postModal: true}),
        ...tap(700, td('post-content'), 300),
        type(1100, 'postContent', DEMO_POST_TEXT, 150),
        scroll(1900, td('post-dropzone')),
        ...tap(2150, td('post-dropzone'), 300),
        count(2550, 'postMedia', 0, 3, 500),
        scroll(2650, td('mp-section')),
        ...tap(3050, td('mp-toggle'), 400),
        set(3550, {mpEnabled: true, mpFilling: true}),
        set(4500, {mpFilling: false, mpFilled: true}),
        scroll(4600, td('mp-type')),
        ...tap(5500, td('post-publish'), 400),
        set(6000, {postSaving: true}),
        set(6450, {postModal: false, postSaving: false, hasPost: true}),
        scroll(6500, td('posts-section')),
        spot(6700, td('post-card')),
      ],
    },
    {
      id: 'groups',
      title: 'קבוצות',
      headline: '3 · הקבוצות נבחרות לבד',
      audio: '/media/tutorial/reel/r4-groups.mp3',
      audioMs: 3600,
      text: 'המערכת בוחרת לבד את הקבוצות הכי מתאימות לתחום שלכם.',
      words: [['המערכת', 0, 560], ['בוחרת', 600, 960], ['לבד', 1040, 1280], ['את', 1320, 1400], ['הקבוצות', 1420, 1960], ['הכי', 1980, 2080], ['מתאימות', 2120, 2520], ['לתחום', 2540, 2880], ['שלכם.', 2920, 3600]],
      init: {hasCampaign: true, hasPost: true},
      cursorStart: {x: 360, y: 600},
      steps: [
        scroll(0, td('campaigns-card')),
        ...tap(100, td('groups-stat'), 400),
        set(600, {groupsModal: true, libLoading: true}),
        set(1300, {libLoading: false, libOn: true}),
        count(1400, 'groupsShown', 0, DEMO_GROUPS.length, 900),
        spot(1400, td('lib-card')),
        spot(2600, null),
        ...tap(2800, td('groups-save'), 450),
        set(3350, {groupsModal: false, groupCount: DEMO_LIBRARY_GROUPS}),
        spot(3400, td('groups-stat')),
      ],
    },
    {
      id: 'connect',
      title: 'חיבור פרופיל',
      headline: '4 · מחברים את הפייסבוק',
      audio: '/media/tutorial/reel/r5-connect.mp3',
      audioMs: 4480,
      text: 'מחברים את פרופיל הפייסבוק, בדיוק כמו התחברות רגילה, ומאשרים בטלפון.',
      words: [['מחברים', 0, 440], ['את', 460, 520], ['פרופיל', 560, 880], ['הפייסבוק,', 920, 1600], ['בדיוק', 1640, 1960], ['כמו', 2000, 2080], ['התחברות', 2120, 2560], ['רגילה,', 2640, 3080], ['ומאשרים', 3200, 3640], ['בטלפון.', 3660, 4480]],
      init: {hasCampaign: true, hasPost: true, groupCount: DEMO_LIBRARY_GROUPS},
      cursorStart: {x: 360, y: 500},
      steps: [
        scroll(0, 'top'),
        ...tap(100, td('connect-btn'), 400),
        set(600, {connectModal: true, connectStage: 'intro'}),
        ...tap(700, td('connect-password'), 300),
        set(1100, {browserOpen: true, fbPage: 'login'}),
        ...tap(1250, td('fb-user'), 250),
        type(1600, 'fbUser', DEMO_FB_EMAIL, 45),
        ...tap(2150, td('fb-pass'), 200),
        type(2450, 'fbPass', 'Dav1d-2024', 40),
        ...tap(2750, td('fb-login'), 250),
        set(3100, {fbBusy: true}),
        set(3450, {fbBusy: false, fbPage: 'checkpoint', phone: 'approve'}),
        spot(3450, td('phone')),
        ...tap(3700, td('phone-yes'), 350),
        set(4150, {phone: 'approved'}),
        spot(4150, null),
        set(4700, {browserOpen: false, phone: 'off', connectStage: 'success', connected: true}),
        ...tap(5000, td('connect-done'), 350),
        set(5450, {connectModal: false, campaignEnabled: true}),
        spot(5500, td('profile-card')),
      ],
    },
    {
      id: 'auto',
      title: 'פרסום אוטומטי',
      headline: '5 · והמערכת מפרסמת בשבילכם',
      audio: '/media/tutorial/reel/r6-auto.mp3',
      audioMs: 5680,
      text: 'ומשם, המערכת מפרסמת בשבילכם כל יום, קבוצה אחרי קבוצה, מהפרופילים שלכם.',
      words: [['ומשם,', 0, 560], ['המערכת', 680, 1307], ['מפרסמת', 1333, 1760], ['בשבילכם', 1800, 2420], ['כל', 2480, 2560], ['יום,', 2640, 2960], ['קבוצה', 3040, 3360], ['אחרי', 3400, 3680], ['קבוצה,', 3840, 4240], ['מהפרופילים', 4360, 5000], ['שלכם.', 5020, 5680]],
      init: {...READY, view: 'schedule', published: 1284, today: 36},
      hideCursor: true,
      steps: [
        scroll(0, 'top'),
        count(300, 'scheduleDone', 0, 7, 5200),
        spot(400, td('sched-0')),
        spot(2300, td('sched-3')),
        spot(4200, td('sched-5')),
      ],
    },
    {
      id: 'cta',
      title: 'FB Zipper',
      headline: 'FB Zipper',
      audio: '/media/tutorial/reel/r7-cta.mp3',
      audioMs: 4800,
      text: 'אתם מוסיפים פוסט, אנחנו דואגים לכל השאר. FB Zipper, נסו עכשיו!',
      words: [['אתם', 0, 280], ['מוסיפים', 300, 680], ['פוסט,', 720, 1200], ['אנחנו', 1240, 1560], ['דואגים', 1600, 1960], ['לכל', 1980, 2160], ['השאר.', 2200, 2800], ['FB', 2933, 3200], ['Zipper,', 3260, 3840], ['נסו', 4000, 4160], ['עכשיו!', 4240, 4800]],
      init: {...READY, outro: true},
      hideCursor: true,
      steps: [],
    },
  ],
  400
)
